import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, ExternalLink, Clock, MoonStar, Users, ArrowDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import PARTNERS from "@/data/partnersData";

const OLIVE = "#3D3B00";
const CREAM = "#F4F1EA";

const IMG = {
  hero: "https://images.unsplash.com/photo-1759038085950-1234ca8f5fed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxob3RlbCUyMHJlY2VwdGlvbiUyMGNoZWNrLWluJTIwZGVzayUyMG1vZGVybnxlbnwwfHx8fDE3ODI0NzIxMTZ8MA&ixlib=rb-4.1.0&q=85",
  portrait: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMG9mZmljZXxlbnwwfHx8fDE3ODI0NzIxMTZ8MA&ixlib=rb-4.1.0&q=85",
};

const QKEYS = ["q1", "q2", "q3", "q4", "q5"];

const T = {
  partner: { nl: "PARTNER", en: "PARTNER", de: "PARTNER", fr: "PARTENAIRE", es: "PARTNER", it: "PARTNER", da: "PARTNER" },
  back: { nl: "Alle partners", en: "All partners", de: "Alle Partner", fr: "Tous les partenaires", es: "Todos los partners", it: "Tutti i partner", da: "Alle partnere" },
  visit: { nl: "Bezoek website", en: "Visit website", de: "Website besuchen", fr: "Visiter le site", es: "Visitar el sitio", it: "Visita il sito", da: "Besøg websitet" },
  configure: { nl: "Configureer jouw kiosk", en: "Configure your kiosk", de: "Kiosk konfigurieren", fr: "Configurez votre kiosque", es: "Configura tu kiosco", it: "Configura il tuo kiosk", da: "Konfigurér din kiosk" },
  about: { nl: "Over de partner", en: "About the partner", de: "Über den Partner", fr: "À propos du partenaire", es: "Sobre el partner", it: "Sul partner", da: "Om partneren" },
  interview: { nl: "Het interview", en: "The interview", de: "Das Interview", fr: "L'interview", es: "La entrevista", it: "L'intervista", da: "Interviewet" },
  how: { nl: "Zo werkt het samen", en: "How it works together", de: "So funktioniert es zusammen", fr: "Comment ça fonctionne ensemble", es: "Cómo funciona en conjunto", it: "Come funziona insieme", da: "Sådan spiller det sammen" },
  result: { nl: "Het resultaat", en: "The result", de: "Das Ergebnis", fr: "Le résultat", es: "El resultado", it: "Il risultato", da: "Resultatet" },
  ctaSub: { nl: "Stel in een paar stappen jouw complete oplossing samen, inclusief deze partner.", en: "Build your complete solution in a few steps, including this partner.", de: "Stellen Sie in wenigen Schritten Ihre komplette Lösung zusammen, inklusive dieses Partners.", fr: "Composez votre solution complète en quelques étapes, ce partenaire inclus.", es: "Crea tu solución completa en unos pasos, incluido este partner.", it: "Crea la tua soluzione completa in pochi passaggi, incluso questo partner.", da: "Sammensæt din komplette løsning på få trin, inklusive denne partner." },
  ctaBtn: { nl: "Start de configurator", en: "Start the configurator", de: "Konfigurator starten", fr: "Lancer le configurateur", es: "Iniciar el configurador", it: "Avvia il configuratore", da: "Start konfiguratoren" },
  disclaimer: { nl: "Illustratief partnerinterview, in afstemming met de partner.", en: "Illustrative partner interview, pending confirmation with the partner.", de: "Illustratives Partnerinterview, in Abstimmung mit dem Partner.", fr: "Interview partenaire illustrative, en cours de validation avec le partenaire.", es: "Entrevista ilustrativa, pendiente de confirmación con el partner.", it: "Intervista illustrativa, in fase di conferma con il partner.", da: "Illustrativt partnerinterview, afventer bekræftelse fra partneren." },
  nodePartner: { nl: "Partner-systeem", en: "Partner system", de: "Partner-System", fr: "Système partenaire", es: "Sistema del partner", it: "Sistema del partner", da: "Partnersystem" },
  nodeCloud: { nl: "RECRA Cloud", en: "RECRA Cloud", de: "RECRA Cloud", fr: "RECRA Cloud", es: "RECRA Cloud", it: "RECRA Cloud", da: "RECRA Cloud" },
  nodeKiosk: { nl: "De incheckzuil", en: "The check-in kiosk", de: "Der Check-in-Kiosk", fr: "La borne de check-in", es: "El kiosco de check-in", it: "Il chiosco di check-in", da: "Check-in-standeren" },
};

const QUESTIONS = {
  q1: { nl: "Welk probleem lossen incheckzuilen op voor jullie klanten?", en: "What problem do check-in kiosks solve for your customers?", de: "Welches Problem lösen Check-in-Kioske für Ihre Kunden?", fr: "Quel problème les bornes de check-in résolvent-elles pour vos clients ?", es: "¿Qué problema resuelven los kioscos de check-in para vuestros clientes?", it: "Quale problema risolvono i chioschi di check-in per i vostri clienti?", da: "Hvilket problem løser check-in-standere for jeres kunder?" },
  q2: { nl: "Hoe werken jullie systeem en de zuil samen?", en: "How do your system and the kiosk work together?", de: "Wie arbeiten Ihr System und der Kiosk zusammen?", fr: "Comment votre système et la borne fonctionnent-ils ensemble ?", es: "¿Cómo funcionan juntos vuestro sistema y el kiosco?", it: "Come lavorano insieme il vostro sistema e il chiosco?", da: "Hvordan arbejder jeres system og standeren sammen?" },
  q3: { nl: "Wat merkt de gast ervan?", en: "What does the guest notice?", de: "Was bemerkt der Gast?", fr: "Qu'est-ce que le client remarque ?", es: "¿Qué nota el huésped?", it: "Cosa nota l'ospite?", da: "Hvad oplever gæsten?" },
  q4: { nl: "Wat betekent het voor de receptie en het personeel?", en: "What does it mean for reception and staff?", de: "Was bedeutet das für Empfang und Personal?", fr: "Qu'est-ce que cela signifie pour la réception et le personnel ?", es: "¿Qué significa para la recepción y el personal?", it: "Cosa significa per la reception e il personale?", da: "Hvad betyder det for receptionen og personalet?" },
  q5: { nl: "Waarom RECRA Solutions als partner?", en: "Why RECRA Solutions as a partner?", de: "Warum RECRA Solutions als Partner?", fr: "Pourquoi RECRA Solutions comme partenaire ?", es: "¿Por qué RECRA Solutions como partner?", it: "Perché RECRA Solutions come partner?", da: "Hvorfor RECRA Solutions som partner?" },
};

const BENEFITS = [
  { icon: Clock, label: { nl: "Kortere wachtrijen", en: "Shorter queues", de: "Kürzere Warteschlangen", fr: "Files plus courtes", es: "Colas más cortas", it: "Code più brevi", da: "Kortere køer" } },
  { icon: MoonStar, label: { nl: "24/7 inchecken", en: "24/7 check-in", de: "Check-in rund um die Uhr", fr: "Check-in 24h/24", es: "Check-in 24/7", it: "Check-in 24/7", da: "Check-in døgnet rundt" } },
  { icon: Users, label: { nl: "Minder werkdruk receptie", en: "Less reception workload", de: "Weniger Last am Empfang", fr: "Moins de charge en réception", es: "Menos carga en recepción", it: "Meno carico alla reception", da: "Mindre pres i receptionen" } },
];

export default function PartnerDetailPage() {
  const { slug } = useParams();
  const { lang } = useApp();
  const data = PARTNERS[slug];
  const t = (o) => (o ? o[lang] || o.en || o.nl : "");
  const prefix = lang && lang !== "nl" ? `/${lang}` : "";

  const pageTitle = data ? `${data.name} × RECRA Solutions` : "";
  React.useEffect(() => {
    if (!pageTitle) return;
    document.title = pageTitle;
    const id = requestAnimationFrame(() => { document.title = pageTitle; });
    return () => cancelAnimationFrame(id);
  }, [pageTitle]);

  if (!data) return <Navigate to={`${prefix}/partners`} replace />;

  const color = data.color;
  const heading = "font-['Cormorant_Garamond']";
  const body = "font-['Manrope']";

  return (
    <div className={`min-h-screen ${body}`} style={{ background: CREAM, color: OLIVE, "--partner-color": color }} data-testid="partner-detail-page">
      <Helmet>
        <meta name="description" content={`${data.name}: ${t(data.positioning)} ${t(data.intro).slice(0, 110)}`} />
        <meta property="og:title" content={`${data.name} × RECRA Solutions`} />
        <meta property="og:description" content={t(data.positioning)} />
        <meta property="og:image" content={IMG.hero} />
      </Helmet>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-12 md:pt-16">
        <Link to={`${prefix}/partners`} className="inline-flex items-center gap-2 text-sm font-semibold opacity-70 hover:opacity-100 transition-opacity" data-testid="partner-back-link">
          <ArrowLeft className="w-4 h-4" /> {t(T.back)}
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center mt-6 md:mt-10">
          <div className="md:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] mb-5" style={{ color }}>{t(T.partner)}</div>
            <span className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 overflow-hidden p-3 border"
                  style={data.logo ? { background: data.logoDark ? color : "#ffffff", borderColor: data.logoDark ? color : "#e6e1d2" } : { background: color, borderColor: color }}>
              {data.logo
                ? <img src={data.logo} alt={`${data.name} logo`} className="max-w-full max-h-full object-contain" />
                : <span className="text-white text-2xl font-bold">{data.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
            </span>
            <h1 className={`${heading} text-5xl md:text-6xl leading-none tracking-tight`}>{data.name}</h1>
            <p className="mt-5 text-lg md:text-xl leading-relaxed opacity-80">{t(data.positioning)}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={data.website} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-bold transition-transform hover:-translate-y-0.5"
                 style={{ background: OLIVE }} data-testid="partner-hero-website">
                {t(T.visit)} <ExternalLink className="w-4 h-4" />
              </a>
              <Link to={`${prefix}/configurator`}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold border-2 transition-transform hover:-translate-y-0.5"
                    style={{ borderColor: OLIVE, color: OLIVE }} data-testid="partner-hero-configure">
                {t(T.configure)} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="md:col-span-7">
            <img src={IMG.hero} alt="" className="w-full h-[44vh] md:h-[68vh] object-cover rounded-3xl" loading="eager" />
          </div>
        </div>
      </section>

      {/* About intro */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs font-bold uppercase tracking-[0.25em] mb-6 opacity-60">{t(T.about)}</div>
        <p className={`${heading} text-2xl md:text-4xl leading-snug`}>{t(data.intro)}</p>
      </section>

      {/* Interview */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-8" data-testid="partner-interview">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <div className="text-xs font-bold uppercase tracking-[0.25em] mb-6" style={{ color }}>{t(T.interview)}</div>
              <img src={IMG.portrait} alt="" className="w-20 h-20 rounded-full object-cover mb-4" loading="lazy" />
              <div className="font-bold">{data.interviewee.name}</div>
              <div className="text-sm opacity-70 mb-6">{t(data.interviewee.role)} · {data.name}</div>
              <blockquote className={`${heading} italic text-2xl md:text-3xl leading-snug border-l-4 pl-5`} style={{ borderColor: color }} data-testid="partner-pullquote">
                “{t(data.quote)}”
              </blockquote>
              <p className="mt-6 text-xs opacity-50">{t(T.disclaimer)}</p>
            </div>
          </div>
          <div className="md:col-span-8 space-y-10">
            {QKEYS.map((qk, i) => (
              <div key={qk} className="border-l-4 pl-6" style={{ borderColor: `${color}55` }} data-testid={`qa-item-${i + 1}`}>
                <h3 className={`${heading} text-xl md:text-2xl mb-3`} data-testid={`qa-question-${i + 1}`}>{t(QUESTIONS[qk])}</h3>
                <p className="text-base md:text-lg leading-relaxed opacity-85">{t(data.answers[qk])}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs font-bold uppercase tracking-[0.25em] mb-8 opacity-60 text-center">{t(T.how)}</div>
        <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-2 mb-10">
          {[t(T.nodePartner), t(T.nodeCloud), t(T.nodeKiosk)].map((node, i) => (
            <React.Fragment key={i}>
              <div className="flex-1 bg-white rounded-2xl border px-6 py-6 text-center" style={{ borderColor: `${OLIVE}1A` }}>
                <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-50 mb-2">{`0${i + 1}`}</div>
                <div className="font-bold text-lg" style={i === 1 ? { color } : undefined}>{node}</div>
              </div>
              {i < 2 && <div className="flex items-center justify-center text-2xl opacity-40 md:rotate-0 rotate-90"><ArrowRight className="w-6 h-6 hidden md:block" /><ArrowDown className="w-6 h-6 md:hidden" /></div>}
            </React.Fragment>
          ))}
        </div>
        <p className="max-w-2xl mx-auto text-center text-base md:text-lg leading-relaxed opacity-85">{t(data.integration)}</p>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="text-xs font-bold uppercase tracking-[0.25em] mb-8 opacity-60 text-center">{t(T.result)}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => {
            const BI = b.icon;
            return (
              <div key={i} className="text-center px-6 py-8 bg-white rounded-2xl border" style={{ borderColor: `${OLIVE}14` }} data-testid={`partner-benefit-${i + 1}`}>
                <BI className="w-9 h-9 mx-auto mb-4" strokeWidth={1.5} style={{ color }} />
                <div className="font-bold text-lg">{t(b.label)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Conversion CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24">
        <div className="rounded-3xl px-8 py-14 md:py-20 text-center" style={{ background: OLIVE }} data-testid="partner-detail-cta">
          <h2 className={`${heading} text-3xl md:text-5xl`} style={{ color: CREAM }}>{t(T.configure)}</h2>
          <p className="mt-4 max-w-xl mx-auto text-sm md:text-base" style={{ color: "#d9d4bf" }}>{t(T.ctaSub)}</p>
          <Link to={`${prefix}/configurator`}
                className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold transition-transform hover:-translate-y-0.5"
                style={{ background: color, color: data.logoDark || ["#3D3B00"].includes(color) ? "#ffffff" : "#ffffff" }}
                data-testid="partner-detail-cta-button">
            {t(T.ctaBtn)} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
